import {useColorTheme} from './color-theme.jsx';
import { brand } from '../brand.js';
import { PALETTE, SEMANTIC, fmtN, DeltaChip, resolveSentiment, resolveTone, toneStyle } from './shared.jsx';
import { Sparkline } from './charts.jsx';
import { useCountUp, MOTION, useEntrance, usePrefersReducedMotion } from './motion.jsx';

// Helper: compact format for potentially large numbers, respects strings
// `locale` = the app language's BCP-47 tag when the host knows it;
// undefined keeps the browser's (historical behaviour).
const displayBig = (v, locale) => {
  if (typeof v !== 'number') return v;
  if (Math.abs(v) >= 1e6) return fmtN(v);
  // One decimal at most. The default (3) printed 18.14 weeks as
  // "18,141" in Spanish, which reads as eighteen thousand (QA D-UIB-1).
  return v.toLocaleString(locale, { maximumFractionDigits: 1 });
};

// ── Sentiment (redesign F3, look v3 2026-10-24) ─────────────
//
// Every KPI variant accepts `sentiment: 'good' | 'bad' | 'neutral'`;
// variants with a compare signal derive it from its SIGN and metrics
// where "up is bad" override it via the prop. Since the v3 look the
// sentiment is carried by the delta chip ONLY (arrow + sign + colour):
// the 3 px semantic top border is gone, so every card keeps one uniform
// 1 px border and one colour signal (reference-theme [data-kpi], craft
// rule "no colour band on cards").


// ── Threshold tinting for the VALUE + the caption line (epic v3) ──
//
// ADDITIVE and opt-in. `kpis.jsx` hardcodes `text-neutral-900` on every
// value and `text-neutral-600` on every subtitle (contrast sweep
// 2026-07-23 — was neutral-400), so the mockups'
// amber 81.7% / green 90.3% and their coloured caption tags are
// unbuildable without this. Both helpers return `undefined` when the
// caller passed nothing, which leaves the existing Tailwind classes in
// charge — every card that does not opt in renders byte-identically.
//
// This is the THRESHOLD axis (`Tone`), not the delta axis
// (`Sentiment`): the top border and the delta chip keep using
// `resolveSentiment` untouched.
const valueToneColor = (spec, value) =>
  spec ? toneStyle(resolveTone(spec, value)).fg : undefined;

// ── Values never truncate ─────────────────────────────────────
//
// A KPI value cut with an ellipsis ("3…" for 30,7 %) reads as a different
// number. The value keeps its digits whole: its row is a size container, the
// font shrinks with the card (`min(30px, 20cqi)`: 30 px from 150 px of usable
// width up, smaller below), a value only breaks between words ("71 vs / 124",
// never inside a number) and the delta chip wraps under the value when both
// do not fit. Titles, subtitles and captions clamp to two lines instead of
// one, and the title row lets its badge drop under the title, so a narrow
// card still says what the number is. The classes are literal on purpose:
// Tailwind only generates what it can read in the source.

// The class size alone (`min(30px, 20cqi)`) ignores how long the value is:
// "$12,345,678.90" in a two-column card overflowed it with no way to read
// the rest (QA F10, 2026-10-26). The longest run that cannot break must
// fit the row: about 0.62 em per tabular digit in Nunito Sans extrabold,
// so the size is also capped at 160cqi / length. Short values are untouched.
function fitValueSize(value, px, cqi) {
  const longest = String(value ?? '').split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0);
  if (longest <= 7) return undefined;
  return `min(${px}px, ${cqi}cqi, ${(160 / longest).toFixed(1)}cqi)`;
}
const valueStyle = (color, size) =>
  color || size ? { ...(color ? { color } : {}), ...(size ? { fontSize: size } : {}) } : undefined;

function KpiCaption({ caption }) {
  if (!caption || !caption.text) return null;
  const color = caption.tone ? toneStyle(caption.tone).fg : undefined;
  return (
    <p className="mt-1 line-clamp-2 text-xs font-semibold" style={color ? { color } : undefined}>
      {caption.text}
    </p>
  );
}

// ── KPI action band ──────────────────────────────────────────
//
// La franja de abajo de la tarjeta: "Ver en Master Matrix →". En el
// reporte de referencia aparece DIEZ veces (4 KPIs + 6 colas), ocupa
// como un tercio del alto de cada tarjeta, y es buena parte de por qué
// esas tarjetas se leen llenas y las nuestras flotaban en aire.
//
// Dos estados, y la diferencia NO es cosmética:
//
//   * con `href` → es un <a> de verdad, borde sólido, hover.
//   * sin `href` → es un <span> con borde PUNTEADO y texto atenuado,
//     no focusable y con `aria-disabled`. Ocupa exactamente el mismo
//     lugar y no finge ser clickeable.
//
// El segundo estado es el que importa: un botón que no navega a ninguna
// parte es peor que no tener botón — el lector hace click, no pasa
// nada, y aprende a desconfiar del resto de la página. El `note`
// explica POR QUÉ no lleva a ningún lado y viaja como `title`.
function KpiAction({ action }) {
  if (!action || !action.label) return null;
  // v3: a footer row under a hairline, left-aligned, violet; pinned to the
  // bottom of the card (mt-auto) so actions line up across a KPI row.
  const base =
    'mt-auto flex min-h-9 w-full items-center gap-1.5 border-t pt-[11px] text-left text-2xs font-semibold leading-tight';
  // In-app navigation (DSA 1.5: "Ver en <tab> →" switches the dashboard
  // tab and applies filters): a real <button>, live even without href.
  if (typeof action.onClick === 'function') {
    return (
      <button data-kpi-action=""
        type="button"
        onClick={(e) => {
          e.preventDefault();
          action.onClick();
        }}
        title={action.note || undefined}
        className={`${base} border-neutral-200 text-primary transition-colors hover:text-primary-ink hover:underline underline-offset-2`}
      >
        {action.label}
      </button>
    );
  }
  if (action.href) {
    return (
      <a data-kpi-action=""
        href={action.href}
        title={action.note || undefined}
        className={`${base} border-neutral-200 text-primary transition-colors hover:text-primary-ink hover:underline underline-offset-2`}
      >
        {action.label}
      </a>
    );
  }
  return (
    <span data-kpi-action=""
      aria-disabled="true"
      title={action.note || undefined}
      className={`${base} cursor-default border-dashed border-neutral-300 text-neutral-500`}
    >
      {action.label}
    </span>
  );
}

// ── KPI Card (basic with optional trend) ──────────────────────
// `delta` (signed number, percent semantics like KPICompact's
// `change`) + optional pre-formatted `deltaLabel` render a semantic
// delta chip next to the value (rule 4). Both optional — without
// them the card renders exactly as before.
export function KPICard({ title, value, subtitle, accent = 'border-accent', sentiment, delta, deltaLabel, valueTone, valueToneValue, caption, action }) {
  const componentTheme = useColorTheme('KPICard');
  const s = resolveSentiment(sentiment, delta);
  const valueColor = valueToneColor(valueTone, valueToneValue);
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
  return (
    <div data-kpi="KPICard" data-color-override={componentTheme.override || undefined}
      // `flex flex-col justify-center`: when the KPI strip stretches the
      // card (h-full in a taller row), the content centers vertically
      // instead of pinning to the top with whitespace below. No-op at
      // intrinsic height (uniform-grid PR-2; same on the variants below).
      // `transform-gpu` + transitioning only transform/shadow (not `all`):
      // every card keeps its own compositor layer, so a neighbour's
      // growing hover shadow no longer forces a re-layer that briefly
      // leaves part of THIS card unpainted (grey band flicker in a KPI row).
      className={`rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] min-h-[140px] min-w-0 flex flex-col justify-start gap-1`}
      style={componentTheme.override ? {"--kpi-color":componentTheme.palette[0]} : undefined}>
      <p className="text-xs font-semibold text-neutral-500 mb-1 line-clamp-2">{title}</p>
      <div className="[container-type:inline-size] flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
        <p className="font-display text-[length:min(30px,20cqi)] font-extrabold leading-[1.2] tracking-[-.8px] text-neutral-800 tabular-nums" style={valueStyle(valueColor, fitValueSize(value, 30, 20))} title={typeof value==='string'?value:undefined}>{value}</p>
        {(hasDelta || deltaLabel != null) && (
          <DeltaChip delta={delta} label={deltaLabel} sentiment={s} className="shrink-0" />
        )}
      </div>
      {subtitle && <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{subtitle}</p>}
      <KpiCaption caption={caption} />
      <KpiAction action={action} />
    </div>
  );
}

// ── KPI Sparkline (value + mini chart + trend) ──────────────────
// The plain-text trend pill is now a semantic DeltaChip (rule 4);
// `trend` drives the arrow direction and the default sentiment.
export function KPISparkline({ title, value, trend, trendValue, sparkData, color, sentiment, entrance = true }) {
  const componentTheme = useColorTheme('KPISparkline');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  // Entrance (ronda 12 sweep): the sparkline fades in on mount.
  // The polyline itself lives inside `Sparkline` (charts.jsx), which
  // does not expose line-draw props, so the entrance is an opacity
  // fade on its wrapper — compositor-only. Hooks before any early
  // return per the motion rules.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const delta = trend === 'up' ? 1 : trend === 'down' ? -1 : 0;
  const s = resolveSentiment(sentiment, trend ? delta : undefined);
  const shown = !entrance || entered;
  return (
    <div data-kpi="KPISparkline"
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] min-h-[140px] min-w-0 flex flex-col justify-start gap-1">
      <div className="flex items-start justify-between gap-3">
        <div className="[container-type:inline-size] min-w-0 flex-1">
          <p className="text-xs font-semibold text-neutral-500 mb-1 line-clamp-2">{title}</p>
          <p className="font-display text-[length:min(30px,20cqi)] font-extrabold leading-[1.2] tracking-[-.8px] text-neutral-800 tabular-nums" style={valueStyle(undefined, fitValueSize(value, 30, 20))}>{value}</p>
          {trendValue && (
            <DeltaChip delta={delta} label={trendValue} sentiment={s} className="mt-1.5" />
          )}
        </div>
        {sparkData && (
          <div
            className="shrink-0"
            style={{
              opacity: shown ? 1 : 0,
              transition: reduced ? undefined : `opacity ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
            }}>
            <Sparkline data={sparkData} color={color} width={72} height={32}/>
          </div>
        )}
      </div>
    </div>
  );
}

// ── KPI Ring (circular progress) ───────────────────────────────
export function KPIRing({ title, value, displayValue, max = 100, color, subtitle, sentiment, entrance = true }) {
  const componentTheme = useColorTheme('KPIRing');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  // Entrance (ronda 12 sweep, mirror of the scorecard rings): the arc
  // first paints fully offset (empty) and the stroke-dashoffset
  // transition sweeps it to its value. Compositor-only. Hooks before
  // any early return per the motion rules.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  // Clamp the GEOMETRY to [0,1] (audit #218): past max the dash wrapped
  // (130% drew ~70%). The label keeps the real value.
  const ratio=Math.max(0, Math.min(1, value/max));
  const r=24, sw=5, circ=2*Math.PI*r, offset=circ*(1-ratio);
  const s = resolveSentiment(sentiment);
  const arcOffset = !entrance || entered ? offset : circ;
  return (
    <div data-kpi="KPIRing"
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] flex items-center gap-4 min-w-0">
      <div className="relative shrink-0">
        <svg width="58" height="58" className="transform -rotate-90" role="img" aria-label={`${Math.round(value)}%`}>
          <circle cx="29" cy="29" r={r} fill="none" stroke={brand.neutral.track} strokeWidth={sw}/>
          <circle cx="29" cy="29" r={r} fill="none" stroke={color} strokeWidth={sw}
            strokeDasharray={circ} strokeDashoffset={arcOffset} strokeLinecap="round"
            style={{ transition: reduced ? undefined : `stroke-dashoffset ${MOTION.durEntrance}ms ${MOTION.easeOut}` }}/>
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-neutral-800">{Math.round(value)}%</span>
      </div>
      <div className="[container-type:inline-size] min-w-0 flex-1">
        <p className="text-xs font-semibold text-neutral-500 line-clamp-2">{title}</p>
        <p className="font-display text-[length:min(20px,14cqi)] font-extrabold text-neutral-800 leading-tight tabular-nums">{displayValue||`${value}%`}</p>
        {subtitle&&<p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── KPI Hero (gradient card) ────────────────────────────────────
export function KPIHero({ title, value, subtitle, icon, gradient, sentiment }) {
  const s = resolveSentiment(sentiment);
  return (
    <div data-kpi="KPIHero"
      className={`rounded-[14px] ${gradient ? `bg-gradient-to-br ${gradient}` : 'bg-neutral-900'} text-white px-5 py-[19px] min-h-[140px] shadow-card min-w-0 flex flex-col justify-start gap-1`}>
      <div className="flex items-start justify-between gap-3">
        <div className="[container-type:inline-size] min-w-0 flex-1">
          <p className="text-xs font-semibold text-white/80 mb-2 line-clamp-2">{title}</p>
          <p className="font-display text-[length:min(30px,20cqi)] font-extrabold leading-[1.2] tracking-[-.8px] tabular-nums" style={valueStyle(undefined, fitValueSize(value, 30, 20))}>{value}</p>
          {subtitle&&<p className="text-xs text-white/80 mt-2 line-clamp-2">{subtitle}</p>}
        </div>
        {icon&&<div className="rounded-xl bg-white/15 p-2.5 shrink-0">{icon}</div>}
      </div>
    </div>
  );
}

// ── KPI Bar (horizontal progress) ──────────────────────────────
export function KPIBar({ title, value, max = 100, color, displayValue, sentiment, entrance = true }) {
  const componentTheme = useColorTheme('KPIBar');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  // Entrance (ronda 12 sweep, mirror of MeterBar in scorecard.jsx):
  // first paint at scaleX(0), then the transform transition grows the
  // fill to its ratio. Compositor-only (transform, no width animation).
  // Hooks before any early return per the motion rules.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const p = Math.min((value/max)*100, 100);
  const s = resolveSentiment(sentiment);
  const grown = !entrance || entered;
  return (
    <div data-kpi="KPIBar"
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] min-h-[140px] min-w-0 flex flex-col justify-start gap-1">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="min-w-0 text-xs font-semibold text-neutral-500 line-clamp-2">{title}</p>
        <p className="text-sm font-bold text-neutral-800 shrink-0 tabular-nums">{displayValue||value}</p>
      </div>
      <div className="h-2.5 rounded-full bg-neutral-100 overflow-hidden">
        <div
          className="h-full origin-left rounded-full"
          style={{
            width: '100%',
            transform: `scaleX(${grown ? p / 100 : 0})`,
            background: color,
            transition: reduced ? undefined : `transform ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
          }}/>
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[11px] text-neutral-600">0</span>
        <span className="text-[11px] text-neutral-600">{max}</span>
      </div>
    </div>
  );
}

// ── KPI Compare (before / after) ───────────────────────────────
// ``format`` controls how values + delta render:
//   - "percent" → current/previous shown as ``62.0%``, delta as
//     ``+0.5 pp`` (puntos porcentuales: current − previous in pp, NOT
//     relative % change). Matches legacy ``Resumen Ejecutivo`` cards.
//   - "number" (default) → toLocaleString, delta as relative %.
// ``unit`` appends after the formatted current/previous (e.g. ``" sem."``
// for week-count KPIs).
//
// Redesign F3: the plain-text ↑/↓ delta block is now a semantic
// DeltaChip inline with the value (approved reference layout), and
// the card carries a 3px sentiment top border. The default sentiment
// follows the SIGN of the delta (up = good); metrics where smaller
// is better pass ``sentiment`` explicitly.
export function KPICompare({ title, current, previous, format = 'number', unit = '', previousLabel = 'Prev', sentiment, valueTone, valueToneValue, caption, entrance = true }) {
  // Entrance (ronda 14): subtle fade+rise of the card CONTENT (the
  // values are pre-computed text here — no count-up to fake), with the
  // DeltaChip fading in 300ms later. Compositor-only; hooks before any
  // early return per the motion rules.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const shown = !entrance || entered;
  const riseStyle = {
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : 'translateY(4px)',
    transition: reduced ? undefined : `opacity ${MOTION.durBase}ms ${MOTION.easeOut}, transform ${MOTION.durBase}ms ${MOTION.easeOut}`,
  };
  // Ronda 15: the main value COUNTS 0 -> current (it arrives numeric
  // here; fmtMain is deterministic, so the landed frame is
  // byte-identical to the static path). The Prev line stays static.
  const counted = useCountUp(typeof current === 'number' ? current : null, { disabled: !entrance });
  const diff = current - previous;
  const valueColor = valueToneColor(valueTone, valueToneValue ?? current);
  const isPercent = format === 'percent';
  const s = resolveSentiment(sentiment, diff);
  const fmtMain = v => {
    if (typeof v !== 'number') return v;
    if (isPercent) return v.toFixed(1) + '%';
    return displayBig(v) + (unit ? ' ' + unit.trim() : '');
  };
  const deltaLabel = (() => {
    if (isPercent) {
      const sign = diff > 0 ? '+' : '';
      return `${sign}${diff.toFixed(1)} pp`;
    }
    if (!previous) return diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${displayBig(diff)}`;
    const pctChange = ((diff / previous) * 100).toFixed(1);
    return `${diff > 0 ? '+' : ''}${pctChange}%`;
  })();
  return (
    <div data-kpi="KPICompare"
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] min-h-[140px] min-w-0 flex flex-col justify-start gap-1">
      <p className="text-xs font-semibold text-neutral-500 mb-2 line-clamp-2">{title}</p>
      <div className="[container-type:inline-size] flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0" style={riseStyle}>
        <p className="font-display text-[length:min(30px,20cqi)] font-extrabold leading-[1.2] tracking-[-.8px] text-neutral-800 tabular-nums" style={valueStyle(valueColor, fitValueSize(fmtMain(current), 30, 20))}>{fmtMain(typeof current === 'number' ? counted : current)}</p>
        <DeltaChip delta={diff} label={deltaLabel} sentiment={s} entrance={entrance} className="shrink-0" />
      </div>
      <p className="text-xs text-neutral-600 mt-1 line-clamp-2" style={riseStyle}>{previousLabel}: {fmtMain(previous)}</p>
      <KpiCaption caption={caption} />
    </div>
  );
}

// ── KPI Core (DSA KpiStrip: signature-colour card with CORE badge) ──
//
// The DigitalShelf strip (maqueta v3.1 `KpiStrip`, prototype v7
// `.kpi.hero`): a white card whose LEFT border and soft diagonal wash
// carry the dashboard's signature colour, a small badge ("CORE") pinned
// top-right on the one KPI the reader should look at first, the value
// big, a semantic delta chip against the previous window (pp for
// percent metrics), one explanatory line under the value and the
// action band ("Ver en Retailers →"). `focused` paints the ring the
// strip uses for the KPI whose analysis is open; the STRIP decides
// whether the badge shows (one focus at a time: focusing another KPI
// hides the CORE badge), this card only draws what it is handed.
//
// `previous` is optional: without it there is no chip and no Prev line
// (the card still reads complete — value + subtitle + action). `color`
// is a CSS colour (hex); the wash is that colour at ~8 % alpha.

export function KPICore({ title, current, previous, format = 'number', unit = '', subtitle, badge, color, focused = false, sentiment, valueTone, valueToneValue, caption, action, previousLabel = 'Prev', entrance = true, trailing, locale }) {
  const componentTheme = useColorTheme('KPICore');
  const clientColors = componentTheme.palette;
  // The focus ring is selection chrome, not data: unless the config or a
  // component theme chose a colour, it follows the tenant's primary
  // (default: the kit violet, the same value as palette S1).
  const ownColor = color !== undefined || !!componentTheme.override;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];
  const ringColor = ownColor ? color : 'rgb(var(--bi-primary))';

  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const shown = !entrance || entered;
  const riseStyle = {
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : 'translateY(4px)',
    transition: reduced ? undefined : `opacity ${MOTION.durBase}ms ${MOTION.easeOut}, transform ${MOTION.durBase}ms ${MOTION.easeOut}`,
  };
  const isNumber = typeof current === 'number' && Number.isFinite(current);
  const hasPrev = isNumber && typeof previous === 'number' && Number.isFinite(previous);
  const counted = useCountUp(isNumber ? current : null, { disabled: !entrance });
  const diff = hasPrev ? current - previous : undefined;
  const s = resolveSentiment(sentiment, diff);
  const isPercent = format === 'percent';
  const valueColor = valueToneColor(valueTone, valueToneValue ?? (isNumber ? current : undefined));
  // One decimal in the app language ("33,1" in ES) when the host passes
  // `locale`; without it, the historical `toFixed` ("33.1").
  const fixed1 = v => (locale
    ? v.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : v.toFixed(1));
  const fmtMain = v => {
    if (typeof v !== 'number') return v;
    if (isPercent) return fixed1(v) + '%';
    return displayBig(v, locale) + (unit ? ' ' + unit.trim() : '');
  };
  const deltaLabel = (() => {
    if (!hasPrev) return null;
    if (isPercent) return `${diff > 0 ? '+' : ''}${fixed1(diff)} pp`;
    if (!previous) return diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${displayBig(diff, locale)}`;
    return `${diff > 0 ? '+' : ''}${fixed1((diff / previous) * 100)}%`;
  })();
  return (
    <div data-kpi="KPICore"
      className={`relative rounded-[14px] border bg-white shadow-card px-5 py-[19px] min-h-[140px] min-w-0 flex flex-col justify-start gap-1${focused ? ' ring-2 ring-inset' : ' border-neutral-200'}`}
      style={{
        '--kpi-color': color, '--kpi-override': componentTheme.override ? color : undefined,
        ...(focused ? { '--tw-ring-color': ringColor, borderColor: ringColor } : {}),
      }}>
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 mb-1">
        <p className="min-w-0 text-xs font-semibold text-neutral-500 line-clamp-2">{title}</p>
        {/* Badge and the host's corner icons (ⓘ tooltip, "ignores
            filters") share ONE flex row, so they lay out next to each
            other instead of the icons being pinned absolutely over the
            badge — that overlap is exactly what this slot removes. */}
        {(badge || trailing) && (
          <span className="flex shrink-0 items-center gap-1.5">
            {badge && (
              <span
                className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary-ink">
                {badge}
              </span>
            )}
            {trailing}
          </span>
        )}
      </div>
      <div className="[container-type:inline-size] flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0" style={riseStyle}>
        <p className="font-display text-[length:min(30px,20cqi)] font-extrabold leading-[1.2] tracking-[-.8px] text-neutral-800 tabular-nums" style={valueStyle(valueColor, fitValueSize(fmtMain(current), 30, 20))}>
          {fmtMain(isNumber ? counted : current)}
        </p>
        {hasPrev && <DeltaChip delta={diff} label={deltaLabel} sentiment={s} entrance={entrance} className="shrink-0" />}
      </div>
      {subtitle && <p className="text-xs text-neutral-600 mt-1.5 line-clamp-2" style={riseStyle}>{subtitle}</p>}
      {hasPrev && <p className="text-[11px] text-neutral-500 mt-0.5 line-clamp-2" style={riseStyle}>{previousLabel}: {fmtMain(previous)}</p>}
      <KpiCaption caption={caption} />
      <KpiAction action={action} />
    </div>
  );
}

// ── KPI Compact (inline row) ────────────────────────────────────
export function KPICompact({ title, value, change, color, sentiment, trailing, stacked = false }) {
  const componentTheme = useColorTheme('KPICompact');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const delta = typeof change === 'number' ? change : 0;
  const s = resolveSentiment(sentiment, typeof change === 'number' ? change : undefined);
  const chip = change !== undefined && (
    <DeltaChip delta={delta} label={`${delta>0?'+':''}${change}%`} sentiment={s} />
  );
  if (stacked) {
    // Stacked (Explorer, 2026-10-29): label on top, value below, both
    // starting at the same left edge, so every tile in a row has the same
    // shape whatever the value's length. The ⓘ is INLINE at the end of
    // the label text, so it follows the last word on one line or two.
    // align-middle centres it on the x-height, which sits 2px below the
    // centre of a 13px snug line; -translate-y-0.5 puts the icon's centre
    // on the text line's centre (measured in develop: 0px off).
    return (
      <div data-kpi="KPICompact" data-layout="stacked"
        className="flex min-h-[78px] gap-3 rounded-[14px] border border-neutral-200 bg-white px-5 py-3.5 shadow-card min-w-0">
        <div aria-hidden="true" className="w-1 max-h-11 self-stretch rounded-full shrink-0" style={{background:color}}/>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[13px] font-semibold leading-snug text-neutral-700">
            {title}
            {trailing && <span className="ml-1 inline-flex -translate-y-0.5 items-center gap-1 align-middle">{trailing}</span>}
          </span>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap font-display text-lg font-extrabold leading-tight text-neutral-800 tabular-nums">{value}</span>
            {chip}
          </div>
        </div>
      </div>
    );
  }
  return (
    // flex-wrap: when the label TEXT (at least 6.5rem, so a two-word
    // label fits in two lines) and the value do not fit side by side, the
    // value drops to its own line INSIDE the same card instead of
    // squeezing the label to "Revenue 1P 3…". The minimum sits on the
    // text, not on the row: the row also holds the colour bar and the ⓘ.
    // `trailing` (the ⓘ) sits right after the title, never on the value.
    <div data-kpi="KPICompact"
      className="flex min-h-[78px] flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-[14px] border border-neutral-200 bg-white px-5 py-3 shadow-card min-w-0">
      <div className="flex flex-1 items-center gap-3">
        <div aria-hidden="true" className="w-1 h-8 rounded-full shrink-0" style={{background:color}}/>
        <span className="min-w-[6.5rem] text-[13px] font-semibold text-neutral-700 line-clamp-2">{title}</span>
        {trailing && <span className="flex shrink-0 items-center gap-1">{trailing}</span>}
      </div>
      <div className="ml-auto flex items-center gap-3 shrink-0">
        <span className="font-display text-base font-extrabold text-neutral-800 tabular-nums">{value}</span>
        {change!==undefined&&(
          <DeltaChip delta={delta} label={`${delta>0?'+':''}${change}%`} sentiment={s} />
        )}
      </div>
    </div>
  );
}

// ── KPI Accent ──────────────────────────────────────────────────
// White card with the KPI's own colour as a top band and a tinted icon
// chip; the number stays dark. For a row of KPIs that each carry a
// colour (the AI Home) without a heavy filled background. `color` is a
// 6-digit hex (the chip tint is that colour at 10 %).
export function KPIAccent({ title, value, subtitle, icon, color = PALETTE[0], entrance = true }) {
  // Mount: the number and its line rise in (same as KPICore); reduced
  // motion shows them at once.
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const shown = !entrance || entered;
  const riseStyle = {
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : 'translateY(4px)',
    transition: reduced ? undefined : `opacity ${MOTION.durBase}ms ${MOTION.easeOut}, transform ${MOTION.durBase}ms ${MOTION.easeOut}`,
  };
  return (
    <div
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-[18px] py-3.5 flex items-center gap-3.5 min-w-0"
      style={{ borderTop: `4px solid ${color}` }}>
      {icon && (
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          style={{ background: `${color}1A`, color }}>
          {icon}
        </div>
      )}
      <div className="[container-type:inline-size] min-w-0 flex-1">
        <p className="text-xs font-semibold text-neutral-500 line-clamp-2">{title}</p>
        <p className="mt-1 font-display text-[length:min(28px,18cqi)] font-extrabold leading-none text-neutral-800 tabular-nums" style={{ ...riseStyle, ...(fitValueSize(value, 28, 18) ? { fontSize: fitValueSize(value, 28, 18) } : {}) }}>{value}</p>
        {subtitle && <p className="text-xs text-neutral-600 mt-1.5 line-clamp-2" style={riseStyle}>{subtitle}</p>}
      </div>
    </div>
  );
}

// ── KPI Icon Stat ───────────────────────────────────────────────
export function KPIIconStat({ title, value, subtitle, icon, iconBg = 'bg-accent/10', iconColor = 'text-accent-dark', sentiment }) {
  const s = resolveSentiment(sentiment);
  return (
    <div data-kpi="KPIIconStat"
      className="rounded-[14px] border border-neutral-200 bg-white shadow-card px-5 py-[19px] flex items-center gap-4 min-w-0">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconBg} ring-1 ring-inset ring-black/5`}>
        <span className={iconColor}>{icon}</span>
      </div>
      <div className="[container-type:inline-size] min-w-0 flex-1">
        <p className="text-xs font-semibold text-neutral-500 line-clamp-2">{title}</p>
        <p className="font-display text-[length:min(20px,14cqi)] font-extrabold text-neutral-800 leading-tight tabular-nums">{value}</p>
        {subtitle&&<p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{subtitle}</p>}
      </div>
    </div>
  );
}
